import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

function useSearchQueryFilter({
    debounceMs = 300,
    onChange = () => {},
}: {
    debounceMs?: number;
    onChange?: () => void;
} = {}) {
    const [searchParams, setSearchParams] = useSearchParams();

    const query = useMemo(() => searchParams.get('query') ?? '', [searchParams]);

    const [searchValue, setSearchValue] = useState(query);

    const setQuery = (newQuery: string) => {
        setSearchParams(
            (params) => {
                if (newQuery.trim() === '') {
                    params.delete('query');
                    return params;
                }

                params.set('query', newQuery.trim());
                return params;
            },
            {
                replace: true,
            }
        );

        onChange();
    };

    const debouncedChangeQuery = useDebouncedCallback(
        (value: string) => setQuery(value),
        debounceMs
    );

    const clearSearch = () => {
        setSearchValue('');
        setQuery('');
    };

    return {
        query,
        input: {
            value: searchValue,
            clear: clearSearch,
            onChange: (val: string) => {
                setSearchValue(val);
                debouncedChangeQuery(val);
            },
        },
    };
}

export { useSearchQueryFilter };
